import { maxWeightMatching, type WeightedEdge } from '../matching/blossom.js';
import { isPlayedResult, pointsFor, type Colour } from '../model/types.js';
import {
  PairingError,
  type Pair,
  type PairingEngine,
  type PairingPlayer,
  type PairingRequest,
  type PairingResult,
} from './types.js';

/**
 * FIDE Double-Swiss System (C.04.5, applied 1 February 2026).
 *
 * Every pairing is a two-game match with alternating colours. In the event
 * model a match occupies two consecutive rounds: odd rounds are paired by
 * this system, even rounds repeat the pairs with colours reversed. The
 * pairing-allocated bye is recorded as a PAB in the first round of the match
 * and a half-point bye in the second (a win and a draw, Article 1.4).
 *
 * Articles: 3.4 PAB first; 3.5 the top scoregroup takes the first set of
 * upfloaters (in lexicographic TPN order) meeting [C4]-[C7]; 3.6 the bracket
 * takes the first pairing in identifier order meeting [C1] and [C8];
 * Article 4 colours.
 */

interface MatchRecord {
  readonly opponent: number | null;
  readonly colour: Colour | null;
  /** At least one game played over the board. */
  readonly played: boolean;
  /** Match decided by forfeit (no game played, scheduled opponent). */
  readonly forfeit: boolean;
  readonly points: number;
  readonly pab: boolean;
}

interface DPlayer {
  readonly id: number;
  readonly score: number;
  readonly matches: readonly MatchRecord[];
  readonly met: ReadonlySet<number>;
  readonly pabEligible: boolean;
  readonly matchesPlayed: number;
  readonly whites: number;
  /** Was this player a floater in the previous match? */
  readonly floatedLast: boolean;
}

function matchesOf(p: PairingPlayer, req: PairingRequest): MatchRecord[] {
  const out: MatchRecord[] = [];
  const h = p.history;
  for (let i = 0; i < h.length; i += 2) {
    const g1 = h[i];
    const g2 = h[i + 1];
    if (g1 === undefined) break;
    const points =
      pointsFor(g1.result, req.pointSystem) +
      (g2 === undefined ? 0 : pointsFor(g2.result, req.pointSystem));
    const played = isPlayedResult(g1.result) || (g2 !== undefined && isPlayedResult(g2.result));
    out.push({
      opponent: g1.opponent,
      colour: played ? g1.colour : null,
      played,
      forfeit: g1.opponent !== null && !played,
      points,
      pab: g1.result === 'U',
    });
  }
  return out;
}

function build(req: PairingRequest): DPlayer[] {
  const records = new Map(req.players.map((p) => [p.id, matchesOf(p, req)]));
  const scoreBefore = (id: number, m: number): number =>
    (records.get(id) ?? []).slice(0, m).reduce((s, r) => s + r.points, 0);
  return req.players
    .filter((p) => p.available)
    .map((p) => {
      const ms = records.get(p.id) ?? [];
      const met = new Set<number>();
      let pabEligible = true;
      for (const m of ms) {
        if (m.opponent !== null && m.played) met.add(m.opponent);
        if (m.pab) pabEligible = false;
        // A match won by forfeit: points scored with no game played.
        if (m.forfeit && m.points > 0) pabEligible = false;
      }
      for (const e of p.history) if (e.result === 'F') pabEligible = false;
      const last = ms.length - 1;
      const lm = ms[last];
      const floatedLast =
        lm !== undefined &&
        lm.opponent !== null &&
        lm.played &&
        scoreBefore(p.id, last) !== scoreBefore(lm.opponent, last);
      return {
        id: p.id,
        score: ms.reduce((s, r) => s + r.points, 0) + (p.acceleration ?? 0),
        matches: ms,
        met,
        pabEligible,
        matchesPlayed: ms.filter((m) => m.played).length,
        whites: ms.filter((m) => m.colour === 'w').length,
        floatedLast,
      };
    })
    .sort((a, b) => b.score - a.score || a.id - b.id);
}

const legal = (a: DPlayer, b: DPlayer): boolean => !a.met.has(b.id) && !b.met.has(a.id);

/** Can these players all be paired legally ([C1])? */
function completable(players: readonly DPlayer[]): boolean {
  if (players.length % 2 === 1) return false;
  if (players.length === 0) return true;
  const edges: WeightedEdge[] = [];
  for (let i = 0; i < players.length; i++) {
    for (let j = i + 1; j < players.length; j++) {
      if (legal(players[i] as DPlayer, players[j] as DPlayer)) edges.push({ u: i, v: j, w: 1n });
    }
  }
  const mate = maxWeightMatching(players.length, edges, { maxCardinality: true });
  return Array.from(mate).every((m) => m >= 0);
}

/** All k-subsets of `xs` in lexicographic order of positions (k is half a bracket). */
function* combinations<T>(xs: readonly T[], k: number, start = 0, acc: T[] = []): Generator<T[]> {
  if (acc.length === k) {
    yield [...acc];
    return;
  }
  for (let i = start; i <= xs.length - (k - acc.length); i++) {
    acc.push(xs[i] as T);
    yield* combinations(xs, k, i + 1, acc);
    acc.pop();
  }
}

/**
 * Article 3.6: the first pairing of the bracket in identifier order (top
 * members ascending, then their bottom members) with the fewest [C8]
 * violations. Returns null if no legal pairing exists.
 */
function pairBracket(
  bracket: readonly DPlayer[],
  upfloaters: ReadonlySet<number>,
  lastRound: boolean,
): [DPlayer, DPlayer][] | null {
  const sorted = [...bracket].sort((a, b) => a.id - b.id);
  const k = sorted.length / 2;
  const c8 = (a: DPlayer, b: DPlayer): number => {
    if (lastRound) return 0;
    let v = 0;
    if (upfloaters.has(a.id) && b.floatedLast) v++;
    if (upfloaters.has(b.id) && a.floatedLast) v++;
    return v;
  };
  // Minimum achievable [C8] violations.
  const edges: WeightedEdge[] = [];
  for (let i = 0; i < sorted.length; i++) {
    for (let j = i + 1; j < sorted.length; j++) {
      const a = sorted[i] as DPlayer;
      const b = sorted[j] as DPlayer;
      if (legal(a, b)) edges.push({ u: i, v: j, w: BigInt(1000 - c8(a, b)) });
    }
  }
  const mate = maxWeightMatching(sorted.length, edges, { maxCardinality: true });
  if (!Array.from(mate).every((m) => m >= 0)) return null;
  let minC8 = 0;
  for (let i = 0; i < sorted.length; i++) {
    const m = mate[i] as number;
    if (m > i) minC8 += c8(sorted[i] as DPlayer, sorted[m] as DPlayer);
  }
  // Enumerate top sets, then bottoms for each top in order, with pruning.
  // (Bounded: pathological brackets fall back to the minimum-[C8] matching.)
  let tried = 0;
  for (const tops of combinations(sorted, k)) {
    if (++tried > 20000) break;
    const topSet = new Set(tops.map((t) => t.id));
    const bottoms = sorted.filter((p) => !topSet.has(p.id));
    const used = new Array<boolean>(bottoms.length).fill(false);
    const cur: [DPlayer, DPlayer][] = [];
    const rec = (i: number, viol: number): boolean => {
      if (viol > minC8) return false;
      if (i === tops.length) return viol === minC8;
      const t = tops[i] as DPlayer;
      for (let j = 0; j < bottoms.length; j++) {
        if (used[j]) continue;
        const b = bottoms[j] as DPlayer;
        if (b.id < t.id || !legal(t, b)) continue;
        used[j] = true;
        cur.push([t, b]);
        if (rec(i + 1, viol + c8(t, b))) return true;
        cur.pop();
        used[j] = false;
      }
      return false;
    };
    if (rec(0, 0)) return cur;
  }
  const fallback: [DPlayer, DPlayer][] = [];
  for (let i = 0; i < sorted.length; i++) {
    const m = mate[i] as number;
    if (m > i) fallback.push([sorted[i] as DPlayer, sorted[m] as DPlayer]);
  }
  return fallback;
}

/** Article 4 colours: returns the colour of the higher-ranked player. */
function colours(h: DPlayer, l: DPlayer, initial: Colour): Colour {
  if (h.matchesPlayed === 0 && l.matchesPlayed === 0) {
    return h.id % 2 === 1 ? initial : initial === 'w' ? 'b' : 'w';
  }
  if (h.whites !== l.whites) return h.whites < l.whites ? 'w' : 'b';
  const hc = h.matches.filter((m) => m.colour !== null).map((m) => m.colour as Colour);
  const lc = l.matches.filter((m) => m.colour !== null).map((m) => m.colour as Colour);
  for (let i = 1; i <= Math.min(hc.length, lc.length); i++) {
    const a = hc[hc.length - i] as Colour;
    const b = lc[lc.length - i] as Colour;
    if (a !== b) return a === 'w' ? 'b' : 'w';
  }
  const hl = hc[hc.length - 1];
  if (hl !== undefined) return hl === 'w' ? 'b' : 'w';
  const ll = lc[lc.length - 1];
  if (ll !== undefined) return ll;
  return h.id % 2 === 1 ? initial : initial === 'w' ? 'b' : 'w';
}

export class DoubleSwissEngine implements PairingEngine {
  readonly id = 'double-swiss';
  readonly name = 'FIDE Double-Swiss (C.04.5)';

  pair(request: PairingRequest): PairingResult {
    if (request.round % 2 === 0) return this.#secondGame(request);
    const players = build(request);
    const lastRound = request.round + 1 >= request.totalRounds;
    const notes: string[] = [];
    let pool = [...players];
    const byes: number[] = [];
    // 3.4 the PAB: legal pairing left, lowest score, most matches, largest TPN.
    if (pool.length % 2 === 1) {
      const cands = [...pool]
        .filter((p) => p.pabEligible)
        .sort((a, b) => a.score - b.score || b.matchesPlayed - a.matchesPlayed || b.id - a.id);
      const bye = cands.find((c) => completable(pool.filter((p) => p !== c)));
      if (bye === undefined)
        throw new PairingError('no legal pairing-allocated bye (C.04.5 3.3.3)');
      byes.push(bye.id);
      pool = pool.filter((p) => p !== bye);
    }
    const pairs: [DPlayer, DPlayer][] = [];
    while (pool.length > 0) {
      const top = pool[0]?.score as number;
      const residents = pool.filter((p) => p.score === top);
      const lower = pool.filter((p) => p.score < top);
      const choice = this.#chooseUpfloaters(residents, lower, lastRound);
      if (choice === null)
        throw new PairingError('the round-pairing cannot be completed (C.04.5 3.3.3)');
      const bracket = [...residents, ...choice];
      const ups = new Set(choice.map((p) => p.id));
      const bp = pairBracket(bracket, ups, lastRound);
      if (bp === null) throw new PairingError('no legal pairing for the bracket');
      pairs.push(...bp);
      const done = new Set(bracket.map((p) => p.id));
      pool = pool.filter((p) => !done.has(p.id));
    }
    const rank = new Map(players.map((p, i) => [p.id, i]));
    const out: Pair[] = pairs
      .map(([a, b]) =>
        (rank.get(a.id) as number) < (rank.get(b.id) as number)
          ? ([a, b] as const)
          : ([b, a] as const),
      )
      .sort(
        ([a1, b1], [a2, b2]) =>
          a2.score - a1.score || a2.score + b2.score - (a1.score + b1.score) || a1.id - a2.id,
      )
      .map(([h, l]) =>
        colours(h, l, request.initialColour) === 'w'
          ? { white: h.id, black: l.id }
          : { white: l.id, black: h.id },
      );
    return { pairs: out, byes, notes };
  }

  /** 3.5: the first set of upfloaters (fewest, highest scores, lexicographic TPNs) meeting [C6]-[C7]. */
  #chooseUpfloaters(
    residents: readonly DPlayer[],
    lower: readonly DPlayer[],
    lastRound: boolean,
  ): DPlayer[] | null {
    const byScore = [...lower].sort((a, b) => b.score - a.score || a.id - b.id);
    const counts = new Map<number, number>();
    for (const p of lower) counts.set(p.score, (counts.get(p.score) ?? 0) + 1);
    const scores = [...counts.keys()].sort((a, b) => b - a);
    const feasible = (set: readonly DPlayer[]): boolean => {
      if (!completable([...residents, ...set])) return false;
      const ids = new Set(set.map((p) => p.id));
      return completable(lower.filter((p) => !ids.has(p.id)));
    };
    for (let k = residents.length % 2; k <= lower.length; k += 2) {
      // Score multisets of size k, best first for [C5] (ascending-sorted, compared lexicographically, larger first).
      const classes: number[][] = [];
      const gen = (i: number, acc: number[]): void => {
        if (acc.length === k) {
          classes.push([...acc].sort((a, b) => a - b));
          return;
        }
        if (i >= scores.length) return;
        const sc = scores[i] as number;
        const max = Math.min(counts.get(sc) as number, k - acc.length);
        for (let c = max; c >= 0; c--) gen(i + 1, [...acc, ...new Array<number>(c).fill(sc)]);
      };
      gen(0, []);
      classes.sort((x, y) => {
        for (let i = 0; i < x.length; i++)
          if (x[i] !== y[i]) return (y[i] as number) - (x[i] as number);
        return 0;
      });
      for (const cls of classes) {
        // Sets of this class in lexicographic order (3.5.3-3.5.4), at most a few hundred.
        const need = new Map<number, number>();
        for (const sc of cls) need.set(sc, (need.get(sc) ?? 0) + 1);
        const sets: DPlayer[][] = [];
        const pick = (i: number, acc: DPlayer[], left: Map<number, number>): void => {
          if (sets.length >= 400) return;
          if (acc.length === k) {
            sets.push([...acc]);
            return;
          }
          for (let j = i; j < byScore.length; j++) {
            const p = byScore[j] as DPlayer;
            const l = left.get(p.score) ?? 0;
            if (l === 0) continue;
            left.set(p.score, l - 1);
            acc.push(p);
            pick(j + 1, acc, left);
            acc.pop();
            left.set(p.score, l);
          }
        };
        pick(0, [], need);
        const ok = sets.filter(feasible);
        if (ok.length === 0) continue;
        // [C6], then [C7]: the first set with the fewest violations.
        let chosen = ok[0] as DPlayer[];
        let bestViol: [number, number] | null = null;
        for (const set of ok) {
          const v: [number, number] = [
            this.#c6(set, lower),
            lastRound ? 0 : set.filter((p) => p.floatedLast).length,
          ];
          if (
            bestViol === null ||
            v[0] < bestViol[0] ||
            (v[0] === bestViol[0] && v[1] < bestViol[1])
          ) {
            bestViol = v;
            chosen = set;
          }
          if (v[0] === 0 && v[1] === 0) break;
        }
        return chosen;
      }
    }
    return null;
  }

  /** [C6]: can the following scoregroup then be paired with the fewest upfloaters its size needs? */
  #c6(set: readonly DPlayer[], lower: readonly DPlayer[]): number {
    const ids = new Set(set.map((p) => p.id));
    const rest = lower.filter((p) => !ids.has(p.id));
    if (rest.length === 0) return 0;
    const nextScore = Math.max(...rest.map((p) => p.score));
    const next = rest.filter((p) => p.score === nextScore);
    const below = rest.filter((p) => p.score < nextScore);
    if (next.length % 2 === 0) return completable(next) && completable(below) ? 0 : 1;
    for (const u of below) {
      if (completable([...next, u]) && completable(below.filter((x) => x !== u))) return 0;
    }
    return 1;
  }

  /** Second game of each match: same pairs, colours reversed. */
  #secondGame(request: PairingRequest): PairingResult {
    const prev = request.round - 2;
    const pairs: Pair[] = [];
    const halfPointByes: number[] = [];
    const seen = new Set<number>();
    for (const p of request.players) {
      const e = p.history[prev];
      if (e === undefined) continue;
      if (e.result === 'U') halfPointByes.push(p.id);
      if (e.opponent === null || seen.has(p.id) || !p.available) continue;
      seen.add(p.id);
      seen.add(e.opponent);
      pairs.push(
        e.colour === 'w' ? { white: e.opponent, black: p.id } : { white: p.id, black: e.opponent },
      );
    }
    return { pairs, byes: [], notes: [], halfPointByes };
  }
}
